import { Injectable, inject } from '@angular/core';
import { MultiselectItem } from '@ui/aos-lib';
import { combineLatest, catchError, of, switchMap, take, Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { ReportGroupFilterListingService } from 'src/app/services/api/report-group-filter/report-group-filter-listing.service';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import {
  COMMODITIES,
  ALARM_GROUP,
  SEVERITY,
  DEVICE_TYPE,
  DATEPICKER_TIME
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  FilterModel,
  FilterModels,
  ListingFiltersSource
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { AlarmsService } from './alarm-data.service';
import {
  isSingleCommodityClient,
  selectCommodities
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable()
export class AlarmsListingFiltersSourceService implements ListingFiltersSource {
  private readonly alarmsService = inject(AlarmsService);
  private readonly store = inject(Store);
  readonly reportGroupFilterService = inject(ReportGroupFilterListingService);
  readonly #isSingleCommodityClient = this.store.selectSignal(isSingleCommodityClient);

  readonly #applicable = 'AMI Health Dashboard';

  getAll(): Observable<FilterModels> {
    return combineLatest([
      this.store.select(selectCommodities).pipe(take(1)),
      this.alarmsService.getAlarmGroupItems(),
      this.getReportGroup()
    ]).pipe(
      switchMap(([commoditiesItems, alarmGroupItems, reportGroupFilter]) => {
        const alarmMultiselectItems: MultiselectItem[] = alarmGroupItems.map(({ groupName }) => ({
          text: translationKeysMap.get(groupName) || groupName,
          value: groupName
        }));
        const alarmGroupFilter: FilterModel = {
          ...ALARM_GROUP,
          items: alarmMultiselectItems,
          selected: alarmMultiselectItems
        };

        const commoditiesMultiselectItems: MultiselectItem[] = commoditiesItems.map(item => ({
          text: translationKeysMap.get(item) || item,
          value: item
        }));
        const commoditiesFilter: FilterModel = {
          ...COMMODITIES,
          items: commoditiesMultiselectItems,
          selected: commoditiesMultiselectItems
        };
        return of([
          reportGroupFilter,
          DATEPICKER_TIME,
          SEVERITY,
          alarmGroupFilter,
          DEVICE_TYPE,
          ...(this.#isSingleCommodityClient() ? [] : [commoditiesFilter])
        ]);
      }),
      catchError(error => {
        return of(error);
      })
    );
  }

  getReportGroup(): Observable<FilterModel> {
    return this.reportGroupFilterService.getFilter(this.#applicable);
  }
}
